import { useCallback, memo, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import { useLottie, useDeleteModal } from '../../hooks'
import animationData from '../../animations/openOrder.json'
import { capitalizeFirstLetter } from '../../lib/utils'
import { DATE_FORMAT, getPaperType, getServiceType, SHIPPING_METHODS, LAB_ORDER_STATUS } from '../../lib/const'

import placeholder from '../../images/noImagePlaceholder.jpg'
import noImage from '../../images/no-Image.png'
import { ReactComponent as UrgentIcon } from '../../images/icons/urgent.svg'
import { ReactComponent as NoteIcon } from '../../images/icons/note.svg'
import { ReactComponent as DeleteIcon } from '../../images/icons/orderDelete.svg'
import { ReactComponent as ShippingIcon } from '../../images/icons/shipping.svg'
import Tooltip from '../common/Tooltip'
import PaperTypeIcon from '../common/PaperTypeIcon'
import ServiceIcon from '../common/ServiceIcon'
import ThreeDotsMenu from './LabOrdersHeader/ThreeDotsAdminMenu'

const lottieOptions = {
  animationData,
  loop: true,
  autoplay: true,
  renderer: 'svg',
  direction: 1,
  duration: 0,
}

const LabOrderCard = ({ order, onDelete, onUpdateOrder, isAdmin, setOpenMenuId, openMenuId }) => {
  const [, container] = useLottie(lottieOptions)
  const { Modal, setIsOpen } = useDeleteModal()
  const [isStatusUpdating, setIsStatusUpdating] = useState(false)
  const { attributes } = order
  const orderDate = attributes.status === 'open' ? attributes.createdAt : attributes.paidAt
  const progress = attributes.progress ?? (attributes.status === 'shipped' ? 100 : 0)
  const testPrintServices = ['test_print', 'test_prints']
  const isTestPrintOnly = attributes.allServices?.some((service) => testPrintServices.includes(service))
    && attributes.allServices.every((service) => [...testPrintServices, 'api_order', 'boutique'].includes(service))
  const usedPapers = isTestPrintOnly ? [] : [...new Set(attributes.papers?.map(getPaperType).filter(Boolean))]
  const usedServices = [...new Set(attributes.allServices?.map(getServiceType).filter(Boolean))]

  const openModal = useCallback(
    (e) => {
      e.preventDefault()
      setIsOpen(true)
    },
    [setIsOpen],
  )

  const handleDelete = useCallback(
    (e) => {
      e.preventDefault()
      onDelete(attributes.id)
    },
    [order, onDelete],
  )

  const handleStatusChange = useCallback(
    async (e) => {
      const newStatus = e.target.value
      if (isStatusUpdating || newStatus === attributes.status) return

      setIsStatusUpdating(true)
      try {
        await onUpdateOrder(attributes.guid || order.id, 'status', newStatus)
      } finally {
        setIsStatusUpdating(false)
      }
    },
    [attributes.guid, attributes.status, isStatusUpdating, onUpdateOrder, order.id],
  )

  const handleStatusMenuOpen = useCallback((e) => {
    const statusBadge = e.currentTarget.parentElement
    const availableWidth = window.innerWidth - statusBadge.getBoundingClientRect().left

    e.currentTarget.classList.toggle('opens-left', e.currentTarget.offsetWidth > availableWidth)
  }, [])

  const hasValidCover = attributes.coverPhotoUrl && !attributes.coverPhotoUrl.includes('/api_lab_order_upload/no-photo.png')
  return (
    <>
      <div className={`order-card ${isAdmin && attributes.urgent ? 'urgent-border' : ''}`}>
        <div className="container-picture-card">
          <img src={hasValidCover ? attributes.coverPhotoUrl : noImage} loading="lazy" decoding="async" alt="" className={hasValidCover ? "image-5" : ""} />
          <Link to={`/lab-orders/${order.id}`} target="_blank" rel="noopener noreferrer" className="link-card w-inline-block">
            <div className="w-layout-grid grid-7">
              <div
                ref={container}
                style={{ transform: 'initial', width: 26, height: 26 }}
              />
              <div>Open Now</div>
            </div>
            {/* {!isAdmin && (

              <div data-w-id="d30cf4d6-488c-cec3-3153-040985f7e223" className="div-block-67">
                <div
                  className="icon w-embed"
                  onClick={openModal}
                  onKeyPress={openModal}
                  role="button"
                >
                  <DeleteIcon />
                </div>
              </div>
            )} */}
          </Link>
          <div className="w-layout-grid grid-16 flex-justify" >
            <div style={{ display: 'flex', gap: '8px' }}>
              {isAdmin && attributes.urgent && (
                <Tooltip title="Urgent">
                  <div className="urgent tippy">
                    <div className="icon small w-embed">
                      <UrgentIcon />
                    </div>
                  </div>
                </Tooltip>
              )}
              {attributes.hasNotes && (
                <Tooltip title="Order Note">
                  <div className="note tippy">
                    <div className="icon small w-embed">
                      <NoteIcon />
                    </div>
                  </div>
                </Tooltip>
              )}
              {isAdmin && attributes.hasAdminNotes && (
                <Tooltip title="Admin Note">
                  <div className="note tippy">
                    <div className="icon small w-embed">
                      <NoteIcon />
                    </div>
                    <div className="div-block-56">
                      <div>A</div>
                    </div>
                  </div>
                </Tooltip>
              )}

              {isAdmin &&
                (attributes.shippingMethod === SHIPPING_METHODS.twoDay ||
                  attributes.shippingMethod === SHIPPING_METHODS.overnight) && (
                  <Tooltip title="Short Run">
                    <div className="rush tippy">
                      <div className="icon small w-embed">
                        <ShippingIcon />
                      </div>
                    </div>
                  </Tooltip>
                  // eslint-disable-next-line indent
                )}
            </div>
            {isAdmin && (
              <div style={{ marginLeft: 'auto' }}>
                <ThreeDotsMenu
                  cardId={order.id}
                  orderGuid={order.attributes?.guid}
                  isUrgent={attributes.urgent}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                  openDeleteModal={openModal}
                  onUpdateOrder={onUpdateOrder}
                />
              </div>
            )}
          </div>

        </div>
        <div className="product-detail-div">
          <div className="div-block-15">
            <div className="div-title-card">
              <div>
                <Link to={`/lab-orders/${order?.id}`} target="_blank" rel="noopener noreferrer" className='link-text'>{attributes?.orderName || '-'}</Link>
              </div>
              {isAdmin ? (
                <div
                  className={`toggle-status ${attributes.status} ${
                    isStatusUpdating ? 'lab-order-card-status-updating' : ''
                  }`}
                >
                  <div>{LAB_ORDER_STATUS[attributes.status]?.label}</div>
                  <select
                    className="order-status-select"
                    value={attributes.status}
                    onChange={handleStatusChange}
                    onMouseDown={handleStatusMenuOpen}
                    onFocus={handleStatusMenuOpen}
                    disabled={isStatusUpdating}
                    aria-label={`Change status for ${attributes.orderName || 'order'}`}
                  >
                    {Object.entries(LAB_ORDER_STATUS).map(([status, { label }]) => (
                      <option key={status} value={status}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className={`toggle-status ${attributes.status}`}>
                  <div>{LAB_ORDER_STATUS[attributes.status]?.label}</div>
                </div>
              )}
            </div>
            <div className="div-title-card">  
              <div>
                <div>{attributes.photographerName ?? '-'}</div>
              </div>
              <div className="w-layout-grid grid-15">
                {usedPapers.map((paper) => (
                  <Tooltip key={paper.label} title={paper.label}>
                    <PaperTypeIcon value={paper.abbreviation} />
                  </Tooltip>
                ))}
              </div>
            </div>
            <div className="div-title-card">
              {attributes?.orderNumber}
            </div>
            <div>{attributes.photographerBusiness ?? '-'}</div>
          </div>
          <div className="divider" />
          <div className="div-sub-info-card">
            <div className="w-layout-grid grid-6 date-div">
              <div>{orderDate ? dayjs(orderDate).format(DATE_FORMAT.monthDayYear) : '-'}</div>
            </div>
            <div className="w-layout-grid grid-15">
              {usedServices.map((service) => (
                <Tooltip key={service.label} title={service.label}>
                  <ServiceIcon
                    classname={service.iconClassname}
                    value={service.abbreviation}
                  />
                </Tooltip>
              ))}
            </div>
          </div>
          <div className="w-layout-grid grid-progress-bar tippy">
            <Tooltip title="Progress">
              <div className="progress-bar">
                <div className="bar" style={{ '--progress-width': `${progress}%` }} />
              </div>
            </Tooltip>
            <div id="w-node-b85c4af7-f736-7b47-03da-55b12a957c10-2283c889">{`${progress}%`}</div>
          </div>
        </div>
      </div>
      <Modal
        title="Deleting Item"
        description="You want to remove this item. This action will not be undone and cannot be reversed."
        onContinue={handleDelete}
        icon={DeleteIcon}
      />
    </>
  )
}

export default memo(LabOrderCard)
